import { useEffect, useMemo } from 'react'
import { BackgroundThumbnail } from '@/components/app/backgrounds/background-thumbnail'
import { SelectCategoryComboBox } from '@components/app/panels/right-panel/combo-box'
import {
  TrayEmptyState,
  TrayGrid,
  TrayLoadingSkeleton,
  TrayPanelLayout,
  useAnyDropdownOpen,
} from '@components/app/panels/right-panel/tray'
import { Label } from '@components/ui/label'
import { useBackgroundsQuery } from '@/api/hooks/use-backgrounds'
import { useBackgroundPlacementOverrides } from '@/api/hooks/use-background-placement-overrides'
import { applyBackgroundPlacementOverrides } from '@/lib/backgrounds/background-placement-session'
import { useBackgroundPlacementActionsOptional } from '@/contexts/background-placement-context'
import type { BackgroundsTypeOption } from '@/lib/backgrounds/types/backgrounds-type-option'
import { filterBackgroundsByKeywords } from '@/lib/backgrounds/filter-backgrounds-by-keywords'
import { filterBackgroundsByFilename } from '@/lib/backgrounds/filter-backgrounds-by-filename'
import { filterBackgroundsByPlaced } from '@/lib/backgrounds/filter-backgrounds-by-placed'
import { suggestBackgroundFilenames } from '@/lib/backgrounds/suggest-background-filenames'
import { BACKGROUNDS_SORT_OPTIONS, sortBackgrounds } from '@/lib/backgrounds/sort-backgrounds'
import type { BackgroundsPanelSettings } from '@/lib/backgrounds/backgrounds-panel-settings'
import { trayColumnCount } from '@/lib/tray/tray-lazy-window'
import { TRAY_TWO_COLUMN_BOX_CLASS } from '@/lib/tray/tray-thumbnail-box'
import { cn } from '@/utils'
import { BackgroundsTrayFilters } from './components/backgrounds-tray-filters'
import { BACKGROUNDS_SINGLE_COLUMN_MAX } from './constants/backgrounds-single-column-max'
import { BACKGROUNDS_SKELETON_COUNT } from './constants/backgrounds-skeleton-count'
import { useBackgroundsPanelSettings } from './hooks/use-backgrounds-panel-settings'

const TYPE_OPTIONS: BackgroundsTypeOption[] = [
  'All backgrounds',
  'Formula colors',
  'Color images',
  'Black and white images',
]

type BackgroundsPanelProps = {
  /** Reports "is one of my dropdowns (Type / Keywords / Sort) or a thumbnail's hover preview
   *  open" — anything Escape should dismiss before the panel itself — so a host panel can drive an
   *  Escape-key priority chain without owning any of this panel's internal state. Mirrors
   *  ClikArtPanel's `onAnyDropdownOpenChange`. */
  onAnyDropdownOpenChange?: (open: boolean) => void
  /**
   * Type, filters, Show filename, sort and the Filters section's expanded state. Pass these to
   * keep them across the panel unmounting — `RightPanel` does, per spread, so closing and
   * reopening the panel on the same spread restores them (see `resolveBackgroundsPanelSettings`).
   * Omitted, the panel keeps them itself and they reset whenever it unmounts.
   */
  settings?: BackgroundsPanelSettings
  onSettingsChange?: (settings: BackgroundsPanelSettings) => void
}

export function BackgroundsPanel({
  onAnyDropdownOpenChange,
  settings: controlledSettings,
  onSettingsChange,
}: BackgroundsPanelProps = {}) {
  const {
    settings,
    setSelectedKeywordIds,
    setFilenameQuery,
    setSizeFilter,
    setPlacedFilter,
    setShowFilename,
    setFiltersOpen,
    setSort,
    handleClearFilters,
    handleTypeChange,
  } = useBackgroundsPanelSettings(controlledSettings, onSettingsChange)

  const {
    type: typeValue,
    keywordIds: selectedKeywordIds,
    filenameQuery,
    size: sizeFilter,
    placed: placedFilter,
    showFilename,
    sort,
    filtersOpen,
  } = settings

  const reportAnyDropdownOpenChange = useAnyDropdownOpen(
    { type: false, keywords: false, filename: false, sort: false, preview: false },
    onAnyDropdownOpenChange
  )

  // Stable (not re-created per thumbnail per render) so a thumbnail's report effect doesn't
  // re-fire on every panel render.
  const reportPreviewOpenChange = useMemo(
    () => reportAnyDropdownOpenChange('preview'),
    [reportAnyDropdownOpenChange]
  )

  // This panel only mounts while it's the open right-hand panel, so mount/unmount is exactly
  // "Backgrounds panel open" — the on-canvas background options toolbars key off it.
  const setBackgroundsPanelOpen = useBackgroundPlacementActionsOptional()?.setBackgroundsPanelOpen

  useEffect(() => {
    if (!setBackgroundsPanelOpen) return

    setBackgroundsPanelOpen(true)
    return () => setBackgroundsPanelOpen(false)
  }, [setBackgroundsPanelOpen])

  // Search keywords, Placed and Search by filename are all client-side and AND-combined, matching
  // legacy's own `searchResultsforClikArtandBackground` (odctrl.js): keywords match a background's
  // cmsKeywordInfo, Placed reads the library response's `placedFlag` (the API has no matching
  // query param), and filename does a plain case-insensitive substring match against displayName.
  const {
    data: libraryBackgrounds,
    isLoading,
    isError,
  } = useBackgroundsQuery(sizeFilter, typeValue)
  // The server's `placedFlag` only refreshes after logout, so this session's own placements and
  // removals are laid over it — the badge and the Placed filter both read the result.
  const placementOverrides = useBackgroundPlacementOverrides()
  const allBackgrounds = useMemo(
    () => applyBackgroundPlacementOverrides(libraryBackgrounds, placementOverrides),
    [libraryBackgrounds, placementOverrides]
  )
  const keywordAndPlacedFiltered = useMemo(
    () =>
      filterBackgroundsByPlaced(
        filterBackgroundsByKeywords(allBackgrounds, selectedKeywordIds),
        placedFilter
      ),
    [allBackgrounds, selectedKeywordIds, placedFilter]
  )
  // Fuzzy filename search: closest matches first, the chosen Sort within each closeness group (and
  // across the whole list when there's no search term).
  const backgrounds = useMemo(
    () =>
      filterBackgroundsByFilename(keywordAndPlacedFiltered, filenameQuery, (group) =>
        sortBackgrounds(group, sort)
      ),
    [keywordAndPlacedFiltered, filenameQuery, sort]
  )
  // Drawn from the same set the grid filters by filename, so every suggestion is a filename that
  // actually has a result.
  const filenameSuggestions = useMemo(
    () => suggestBackgroundFilenames(keywordAndPlacedFiltered, filenameQuery),
    [keywordAndPlacedFiltered, filenameQuery]
  )

  // 1 column at ≤25 results, 2 columns beyond that (`trayColumnCount`, with the Backgrounds limit
  // rather than the tray default of 10). Counts the filtered results, so a search or filter that
  // narrows the list to 25 or fewer switches to one column.
  const columnCount = trayColumnCount(backgrounds.length, BACKGROUNDS_SINGLE_COLUMN_MAX)

  const activeFilterCount =
    selectedKeywordIds.length +
    [filenameQuery.trim() !== '', sizeFilter !== 'any', placedFilter !== 'any'].filter(Boolean)
      .length

  // Formula colors are plain swatches with no CMS keywords, so the keyword picker is hidden for
  // that type (and switching to it clears every applied filter — see `handleTypeChange`).
  const keywordsSearchable = typeValue !== 'Formula colors'

  return (
    <TrayPanelLayout
      picker={
        // Type
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium leading-none text-foreground">Type</Label>
          <SelectCategoryComboBox
            options={TYPE_OPTIONS}
            placeholder="All backgrounds"
            value={typeValue}
            onValueChange={(next) => handleTypeChange(next as BackgroundsTypeOption)}
            onOpenChange={reportAnyDropdownOpenChange('type')}
            focusSearchOnOpen
          />
        </div>
      }
      filtersOpen={filtersOpen}
      onFiltersOpenChange={setFiltersOpen}
      activeFilterCount={activeFilterCount}
      onClearFilters={handleClearFilters}
      filters={
        <BackgroundsTrayFilters
          keywordsSearchable={keywordsSearchable}
          selectedKeywordIds={selectedKeywordIds}
          onSelectedKeywordIdsChange={setSelectedKeywordIds}
          onKeywordsOpenChange={reportAnyDropdownOpenChange('keywords')}
          filenameQuery={filenameQuery}
          onFilenameQueryChange={setFilenameQuery}
          filenameSuggestions={filenameSuggestions}
          onFilenameSuggestionsOpenChange={reportAnyDropdownOpenChange('filename')}
          sizeFilter={sizeFilter}
          onSizeFilterChange={setSizeFilter}
          placedFilter={placedFilter}
          onPlacedFilterChange={setPlacedFilter}
        />
      }
      showFilename={showFilename}
      onShowFilenameChange={setShowFilename}
      resultCount={backgrounds.length}
      showResultCount={!isLoading}
      sort={{
        value: sort,
        options: BACKGROUNDS_SORT_OPTIONS,
        onValueChange: setSort,
        ariaLabel: 'Sort backgrounds',
        listboxLabel: 'Sort backgrounds by',
        onOpenChange: reportAnyDropdownOpenChange('sort'),
      }}
    >
      {isLoading ? (
        // The first load is the unfiltered library — far over 25 backgrounds — so it lands in the
        // two-column grid; the placeholders match that and BackgroundThumbnail's card shell.
        <TrayLoadingSkeleton
          label="Loading backgrounds"
          count={BACKGROUNDS_SKELETON_COUNT}
          columns={2}
          framed
          itemClassName={cn(TRAY_TWO_COLUMN_BOX_CLASS, 'motion-reduce:animate-none')}
        />
      ) : isError ? (
        <p className="px-1 py-4 text-sm text-destructive">Unable to load backgrounds.</p>
      ) : backgrounds.length === 0 ? (
        <TrayEmptyState title="No backgrounds found" description="Try adjusting your filters" />
      ) : (
        <TrayGrid columns={columnCount}>
          {backgrounds.map((background) => (
            <BackgroundThumbnail
              key={background.backgroundId}
              background={background}
              showFileName={showFilename}
              columns={columnCount}
              onPreviewOpenChange={reportPreviewOpenChange}
            />
          ))}
        </TrayGrid>
      )}
    </TrayPanelLayout>
  )
}
